import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Copy, FileText, Pencil, Share2, Trash2, Wand2, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { formService } from '../../services/formService';

const parseEmailList = (raw = '') => {
  const normalized = raw
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .filter((item, index, all) => all.indexOf(item) === index);
  return normalized.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getVisibilityBadgeClass = (visibility) => {
  if (visibility === 'public') return 'bg-emerald-50 text-emerald-700';
  if (visibility === 'restricted') return 'bg-amber-50 text-amber-700';
  return 'bg-slate-100 text-slate-500';
};

const FormsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;

    const loadForms = async () => {
      setLoading(true);
      try {
        const data = await formService.getUserForms(user.uid);
        setForms(data);
      } catch (err) {
        setError(err?.message || 'Failed to load forms');
      } finally {
        setLoading(false);
      }
    };

    loadForms();
  }, [user]);

  const handleShare = async (form) => {
    if (form.visibility !== 'public') {
      toast.error('Make the form public to share.');
      return;
    }
    const url = `${window.location.origin}/form/${form.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: form.title, url });
        return;
      } catch (err) {
        console.warn('Share cancelled:', err);
      }
    }
    await navigator.clipboard.writeText(url);
    toast.success('Share link copied to clipboard.');
  };

  const handleSetVisibility = async (form, nextVisibility) => {
    if (!nextVisibility || nextVisibility === form.visibility) return;
    try {
      if (nextVisibility === 'restricted') {
        const current = Array.isArray(form.allowedEmails) ? form.allowedEmails.join(', ') : '';
        const raw = window.prompt('Enter allowed emails (comma-separated) for restricted access:', current);
        if (raw === null) return;
        const allowedEmails = parseEmailList(raw);
        if (!allowedEmails.length) {
          toast.error('Add at least one valid email for restricted access.');
          return;
        }
        const updates = { visibility: 'restricted', allowedEmails };
        await formService.updateForm(form.id, updates);
        await formService.publishForm(
          form.id,
          {
            ...updates,
            files: { sourcePdf: form.sourcePdf || null },
            smartDocument: form.smartDocument || null,
            originalDocumentId: form.originalDocumentId || form.documentId || form.id
          },
          user?.uid
        );
        setForms((prev) => prev.map((item) => (item.id === form.id ? { ...item, ...updates } : item)));
        toast.success('Form access is now restricted.');
        return;
      }

      const updates = {
        visibility: nextVisibility,
        allowedEmails: []
      };
      await formService.updateForm(form.id, updates);
      await formService.publishForm(
        form.id,
        {
          ...updates,
          files: { sourcePdf: form.sourcePdf || null },
          smartDocument: form.smartDocument || null,
          originalDocumentId: form.originalDocumentId || form.documentId || form.id
        },
        user?.uid
      );
      setForms((prev) => prev.map((item) => (item.id === form.id ? { ...item, ...updates } : item)));
      toast.success(`Form is now ${nextVisibility}.`);
    } catch (err) {
      toast.error(err?.message || 'Failed to update visibility.');
    }
  };

  const handleDelete = async (form) => {
    const confirmed = window.confirm(`Delete "${form.title}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await formService.deleteForm(form.id, form.sourcePdf?.path);
      setForms((prev) => prev.filter((item) => item.id !== form.id));
      toast.success('Form deleted.');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete form.');
    }
  };

  const handleClone = async (form) => {
    try {
      const clonedForm = await formService.cloneForm(form.id, user?.uid);
      setForms((prev) => [clonedForm, ...prev]);
      toast.success('Form cloned.');
    } catch (err) {
      toast.error(err?.message || 'Failed to clone form.');
    }
  };

  return (
    <div className="space-y-10">
      <header className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-blue-700">
            <Zap className="h-4 w-4" />
            AI Forms
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-[42px]">Forms Library</h2>
          <p className="max-w-3xl text-[15px] text-slate-500 sm:text-lg">
            Manage your shareable, fillable forms and create new ones with ease.
          </p>
        </div>

        <div className="flex flex-wrap gap-4 lg:justify-end">
          <button
            onClick={() => navigate('/forms/smart')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-800 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-900"
          >
            <Wand2 className="h-4 w-4" />
            Smart Processor
          </button>
        </div>
      </header>

      {loading && (
        <div className="rounded-[20px] border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          Loading forms...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-[20px] border border-rose-200 bg-rose-50 p-6 text-rose-700 shadow-sm">
          {error}
        </div>
      )}

      {!loading && !error && forms.length === 0 && (
        <div className="grid gap-6 rounded-[20px] border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
            <FileText className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">No forms yet</h2>
            <p className="mt-2 text-sm text-slate-500">Start by converting a PDF and we will handle the layout and fields.</p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <button
              onClick={() => navigate('/forms/smart')}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-800 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-900"
            >
              <Wand2 className="h-4 w-4" />
              Smart Processor
            </button>
            <button
              onClick={() => navigate('/doc-tools')}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Browse Tools
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {!loading && !error && forms.length > 0 && (
        <div className="grid gap-8 lg:grid-cols-2">
          {forms.map((form) => (
            <article key={form.id} className="rounded-[10px] border border-slate-200 bg-white p-10 shadow-md">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-[21px] font-bold leading-tight text-slate-900 sm:text-[22px]">{form.title}</h3>
                    <span className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] ${getVisibilityBadgeClass(form.visibility)}`}>
                      {form.visibility === 'public' ? 'Public' : form.visibility === 'restricted' ? 'Restricted' : 'Private'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-medium text-slate-500">
                    {form.fields?.length || 0} fields • Updated {formatDate(form.updatedAt || form.createdAt)}
                  </p>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Access Control</p>
                  <select
                    value={form.visibility || 'private'}
                    onChange={(e) => handleSetVisibility(form, e.target.value)}
                    className="min-w-[150px] rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 focus:border-blue-300 focus:ring-0"
                  >
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                    <option value="restricted">Restricted</option>
                  </select>
                </div>
                {form.visibility === 'restricted' && (
                  <p className="mt-3 text-xs text-amber-700">
                    {Array.isArray(form.allowedEmails) ? form.allowedEmails.length : 0} allowed email(s)
                  </p>
                )}
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3 xl:grid-cols-[auto_auto_auto_auto_1fr]">
                <button
                  onClick={() => navigate(`/form/${form.id}`)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                >
                  Open
                </button>
                <button
                  onClick={() => navigate(`/forms/smart/${form.id}/edit`)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleShare(form)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
                <button
                  onClick={() => handleClone(form)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Copy className="h-4 w-4" />
                  Clone
                </button>
                <button
                  onClick={() => handleDelete(form)}
                  className="col-span-2 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 px-4 py-3 text-sm font-semibold text-rose-500 transition-colors hover:bg-rose-50 xl:col-span-1 xl:justify-self-end"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormsList;
